import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import WorkforceTopControl from '../workforce/WorkforceTopControl';
import { useWarmTheme } from '../../theme/ThemeContext';
import { Search, Bell, HelpCircle, Plus, ChevronDown, ShieldCheck, LogOut, ArrowRight, Menu, Camera, Users, UserCheck, Mail, Phone, Loader2, CornerDownLeft, Clock, Sparkles, Palette, Moon, Sun } from 'lucide-react';
import { cn } from '../../lib/utils';
import { CATEGORY_MAP, TOOL_BY_KEY } from './toolRegistry';
import { useAuth } from '../../contexts/AuthContext';
import { uploadAvatarFile } from '../../lib/avatarUpload';

const API = process.env.REACT_APP_BACKEND_URL;
const prettyStatus = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Clean enterprise top bar with a central global command bar that searches
// tools/pages (client-side registry) plus live clients & leads (backend), and
// surfaces recent clients/leads + suggested tools before the user types.
const AppTopBar = ({ user, tools, recentKeys = [], favouriteKeys = [], searchRef, onLaunch, onNav, onLogout, onMenu, isAdmin }) => {
  const { refreshUser, token } = useAuth();
  const warm = useWarmTheme();
  const [q, setQ] = useState('');
  const [menu, setMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [remote, setRemote] = useState({ clients: [], leads: [] });
  const [suggest, setSuggest] = useState({ clients: [], leads: [] });
  const [suggestLoaded, setSuggestLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);
  const boxRef = useRef(null);
  const roleLabel = (user?.role || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const initials = (user?.name || 'U').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadAvatarFile(file);
      await refreshUser();
      setUploading(false);
    } catch (err) {
      alert('Could not update photo. Please try a smaller image.');
      setUploading(false);
    }
  };

  const hasQuery = q.trim().length > 0;

  // Tools & pages — instant client-side match against the registry.
  const toolResults = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return tools.filter((t) => `${t.title} ${t.desc} ${t.keywords || ''} ${t.category}`.toLowerCase().includes(s)).slice(0, 6);
  }, [q, tools]);

  // Suggested tools (before typing): recently used → favourites → first allowed.
  const allowedByKey = useMemo(() => new Set(tools.map((t) => t.key)), [tools]);
  const suggestedTools = useMemo(() => {
    const out = [];
    const seen = new Set();
    const push = (key) => {
      if (seen.has(key) || !allowedByKey.has(key)) return;
      const t = TOOL_BY_KEY[key];
      if (t) { seen.add(key); out.push(t); }
    };
    (recentKeys || []).forEach(push);
    (favouriteKeys || []).forEach(push);
    tools.forEach((t) => push(t.key));
    return out.slice(0, 6);
  }, [recentKeys, favouriteKeys, tools, allowedByKey]);

  // Clients & leads — debounced backend search.
  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setRemote({ clients: [], leads: [] }); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/search/global?q=${encodeURIComponent(s)}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('search failed');
        const data = await res.json();
        if (!cancelled) setRemote({ clients: data.clients || [], leads: data.leads || [] });
      } catch {
        if (!cancelled) setRemote({ clients: [], leads: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, token]);

  // Fetch recent clients/leads once, when the empty bar is first opened.
  useEffect(() => {
    if (!open || hasQuery || suggestLoaded || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/search/suggestions`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('suggest failed');
        const data = await res.json();
        if (!cancelled) { setSuggest({ clients: data.clients || [], leads: data.leads || [] }); setSuggestLoaded(true); }
      } catch {
        if (!cancelled) setSuggestLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, hasQuery, suggestLoaded, token]);

  // Which record sets are active in the panel right now.
  const clientRows = hasQuery ? remote.clients : suggest.clients;
  const leadRows = hasQuery ? remote.leads : suggest.leads;
  const toolRows = hasQuery ? toolResults : suggestedTools;

  // Flat ordered list drives keyboard navigation across all categories.
  const flat = useMemo(() => {
    const items = [];
    clientRows.forEach((c) => items.push({ kind: 'client', id: c.id, data: c }));
    leadRows.forEach((l) => items.push({ kind: 'lead', id: l.id, data: l }));
    toolRows.forEach((t) => items.push({ kind: 'tool', id: t.key, data: t }));
    return items;
  }, [clientRows, leadRows, toolRows]);

  useEffect(() => { setActive(0); }, [flat.length, q]);

  const showPanel = open && flat.length > 0 || (open && hasQuery);

  const openItem = useCallback((item) => {
    if (!item) return;
    setQ(''); setOpen(false); setRemote({ clients: [], leads: [] });
    if (item.kind === 'tool') onLaunch(item.data);
    else onNav(item.data.route);
  }, [onLaunch, onNav]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setQ(''); setOpen(false); searchRef?.current?.blur(); return; }
    if (!flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % flat.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i - 1 + flat.length) % flat.length); }
    else if (e.key === 'Enter') { e.preventDefault(); openItem(flat[active] || flat[0]); }
  };

  const onBlur = (e) => {
    if (boxRef.current && !boxRef.current.contains(e.relatedTarget)) setOpen(false);
  };

  let idx = -1; // running index synced with `flat` for highlight
  const Row = ({ item, children }) => {
    idx += 1;
    const i = idx;
    const isActive = i === active;
    return (
      <button
        type="button"
        onMouseEnter={() => setActive(i)}
        onMouseDown={(e) => { e.preventDefault(); openItem(item); }}
        className={cn('w-full flex items-center gap-3 px-3.5 py-2.5 text-left transition-colors', isActive ? 'bg-[#3A6AFF]/[0.07]' : 'hover:bg-slate-50')}
        data-testid={`command-result-${item.kind}-${item.id}`}
      >
        {children}
        {isActive && <CornerDownLeft className="w-3.5 h-3.5 text-[#3A6AFF] shrink-0" />}
      </button>
    );
  };

  const SectionLabel = ({ icon: Icon, children }) => (
    <div className="px-3.5 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">{Icon && <Icon className="w-3 h-3" />}{children}</div>
  );

  const Contact = ({ email, phone }) => (
    <span className="hidden sm:flex items-center gap-3 text-[11px] text-slate-400 shrink-0 max-w-[240px]">
      {email && <span className="inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3 shrink-0" /><span className="truncate">{email}</span></span>}
      {phone && <span className="inline-flex items-center gap-1 shrink-0"><Phone className="w-3 h-3" />{phone}</span>}
    </span>
  );

  const ClientRow = (c) => (
    <Row key={`c-${c.id}`} item={{ kind: 'client', id: c.id, data: c }}>
      <span className="w-7 h-7 rounded-md bg-[#FFE4E6] text-[#E11D6B] flex items-center justify-center shrink-0"><Users className="w-3.5 h-3.5" /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-slate-800 truncate">{c.name}{c.status && <span className="ml-2 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 align-middle">{c.status}</span>}</span>
      </span>
      <Contact email={c.email} phone={c.phone} />
    </Row>
  );

  const LeadRow = (l) => (
    <Row key={`l-${l.id}`} item={{ kind: 'lead', id: l.id, data: l }}>
      <span className="w-7 h-7 rounded-md bg-[#E1EBFF] text-[#3A6AFF] flex items-center justify-center shrink-0"><UserCheck className="w-3.5 h-3.5" /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-slate-800 truncate">{l.name}{l.status && <span className="ml-2 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 align-middle">{prettyStatus(l.status)}</span>}</span>
      </span>
      <Contact email={l.email} phone={l.phone} />
    </Row>
  );

  const ToolRow = (t) => {
    const cat = CATEGORY_MAP[t.category] || {};
    const Icon = t.icon;
    return (
      <Row key={`t-${t.key}`} item={{ kind: 'tool', id: t.key, data: t }}>
        <span className={cn('w-7 h-7 rounded-md flex items-center justify-center shrink-0', cat.badge)}><Icon className="w-3.5 h-3.5" /></span>
        <span className="text-[13px] font-medium text-slate-800 flex-1 truncate">{t.title}</span>
        <span className="text-[11px] text-slate-400 truncate hidden sm:block">{cat.label}</span>
        <ArrowRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
      </Row>
    );
  };

  const nothing = hasQuery && !loading && flat.length === 0;

  return (
    <header className="min-h-14 shrink-0 bg-white border-b border-slate-200/80 flex items-center gap-4 px-5 font-sf relative z-30" style={{ paddingTop: 'env(safe-area-inset-top)' }} data-testid="app-topbar">
      <button type="button" onClick={onMenu} className="lg:hidden w-9 h-9 -ml-1.5 rounded-lg text-slate-600 hover:bg-slate-100 flex items-center justify-center transition-colors" data-testid="topbar-menu-toggle" aria-label="Open navigation">
        <Menu className="w-5 h-5" />
      </button>
      {/* Context */}
      <div className="hidden md:flex items-center gap-2 text-[13px] shrink-0">
        <span className="text-slate-600 font-medium">{roleLabel || 'Platform'}</span>
      </div>

      {/* Command bar */}
      <div ref={boxRef} className="flex-1 max-w-2xl mx-auto relative" onBlur={onBlur}>
        {loading ? (
          <Loader2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#3A6AFF] animate-spin pointer-events-none" />
        ) : (
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        )}
        <input
          ref={searchRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search DerwentAI — clients, phone numbers, leads, tools…"
          className="w-full pl-10 pr-14 py-2 rounded-lg bg-[#F5F6F8] border border-slate-200 text-[13px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3A6AFF]/25 focus:border-[#3A6AFF]/40 focus:bg-white transition-all"
          data-testid="global-command-input"
          autoComplete="off"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[11px] text-slate-400 bg-white border border-slate-200 px-1.5 py-0.5 rounded hidden sm:block">⌘K</span>

        {showPanel && (
          <div className="absolute left-0 right-0 top-full mt-1.5 bg-white rounded-xl border border-slate-200 shadow-[0_16px_40px_-12px_rgba(15,23,42,0.2)] overflow-hidden max-h-[70vh] overflow-y-auto" data-testid="command-results">
            {nothing && (
              <div className="px-4 py-6 text-center text-[13px] text-slate-400" data-testid="command-empty">No matches for “{q.trim()}”.</div>
            )}

            {clientRows.length > 0 && <>
              <SectionLabel icon={hasQuery ? undefined : Clock}>{hasQuery ? 'Clients' : 'Recent clients'}</SectionLabel>
              {clientRows.map(ClientRow)}
            </>}

            {leadRows.length > 0 && <>
              <SectionLabel icon={hasQuery ? undefined : Clock}>{hasQuery ? 'Leads' : 'Recent leads'}</SectionLabel>
              {leadRows.map(LeadRow)}
            </>}

            {toolRows.length > 0 && <>
              <SectionLabel icon={hasQuery ? undefined : Sparkles}>{hasQuery ? 'Tools & Pages' : 'Suggested tools'}</SectionLabel>
              {toolRows.map(ToolRow)}
            </>}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 shrink-0">
        <WorkforceTopControl />
        <button type="button" className="hidden sm:inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#3A6AFF] text-white text-[13px] font-semibold hover:bg-[#2f59e0] transition-colors" data-testid="topbar-create">
          <Plus className="w-4 h-4" /> Create
        </button>
        <button type="button" className="w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 flex items-center justify-center transition-colors" data-testid="topbar-notifications" aria-label="Notifications">
          <Bell className="w-[18px] h-[18px]" />
        </button>
        <button type="button" className="w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 hidden sm:flex items-center justify-center transition-colors" data-testid="topbar-help" aria-label="Help">
          <HelpCircle className="w-[18px] h-[18px]" />
        </button>
        <div className="relative">
          <button type="button" onClick={() => setMenu((v) => !v)} className="flex items-center gap-1.5 pl-1 pr-1.5 h-8 rounded-lg hover:bg-slate-100 transition-colors" data-testid="topbar-avatar">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt={user?.name || 'User'} className="w-7 h-7 rounded-full object-cover ring-1 ring-slate-200" />
            ) : (
              <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#5A8BFF] to-[#3A6AFF] flex items-center justify-center text-[11px] font-bold text-white">{initials}</span>
            )}
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </button>
          {menu && (
            <div className="absolute right-0 top-full mt-1.5 w-52 bg-white rounded-xl border border-slate-200 shadow-[0_16px_40px_-12px_rgba(15,23,42,0.25)] overflow-hidden z-50" data-testid="topbar-avatar-menu">
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-[13px] font-semibold text-slate-900 truncate">{user?.name}</p>
                <p className="text-[11px] text-slate-400">{roleLabel}</p>
              </div>
              {isAdmin && isAdmin() && (
                <button onClick={() => { onNav('/security'); setMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-blue-50 transition-colors" data-testid="topbar-menu-security">
                  <ShieldCheck className="w-4 h-4 text-[#3A6AFF]" /><span className="text-[13px] font-medium text-slate-700">Security Centre</span>
                </button>
              )}
              <button onClick={() => fileRef.current?.click()} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors" data-testid="topbar-menu-photo">
                <Camera className="w-4 h-4 text-slate-500" /><span className="text-[13px] font-medium text-slate-700">{uploading ? 'Uploading…' : 'Change photo'}</span>
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="avatar-file-input" />
              {warm.flagEnabled && (
                <button onClick={() => warm.setMyTheme(warm.effective === 'apple' ? 'default' : 'apple')} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors border-t border-slate-100" data-testid="topbar-menu-theme-toggle">
                  <Palette className="w-4 h-4 text-slate-500" />
                  <span className="text-[13px] font-medium text-slate-700 flex-1">{warm.effective === 'apple' ? 'Use classic look' : 'Try the Apple look'}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${warm.effective === 'apple' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{warm.effective === 'apple' ? 'ON' : 'OFF'}</span>
                </button>
              )}
              {warm.effective === 'apple' && (
                <button onClick={() => warm.toggleMode()} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors" data-testid="topbar-menu-darkmode-toggle">
                  {warm.mode === 'dark' ? <Sun className="w-4 h-4 text-amber-500" /> : <Moon className="w-4 h-4 text-slate-500" />}
                  <span className="text-[13px] font-medium text-slate-700 flex-1">{warm.mode === 'dark' ? 'Switch to light' : 'Switch to dark'}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${warm.mode === 'dark' ? 'bg-slate-800 text-slate-100' : 'bg-slate-100 text-slate-500'}`}>{warm.mode === 'dark' ? 'NIGHT' : 'DAY'}</span>
                </button>
              )}
              {warm.isAdmin && (
                <div className="border-t border-slate-100 px-4 py-2.5 space-y-2" data-testid="topbar-menu-theme-admin">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Apple theme (admin)</p>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-[12.5px] text-slate-600">Enable for team</span>
                    <input type="checkbox" checked={warm.flagEnabled} onChange={(e) => warm.setAdmin({ enabled: e.target.checked })} data-testid="warm-flag-enable" />
                  </label>
                  {warm.flagEnabled && (
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-[12.5px] text-slate-600">Make default for all</span>
                      <input type="checkbox" checked={warm.globalDefault} onChange={(e) => warm.setAdmin({ default_on: e.target.checked })} data-testid="warm-flag-default" />
                    </label>
                  )}
                </div>
              )}
              <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors border-t border-slate-100" data-testid="topbar-menu-logout">
                <LogOut className="w-4 h-4 text-slate-500" /><span className="text-[13px] font-medium text-slate-700">Log out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppTopBar;
